import { useEffect, useState } from "react";
import { Star, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, fmtErr } from "@/lib/api";

const EMPTY = { client_name: "", age_range: "", program: "", text: "", capability_improvement: "", status: "draft" };

export default function TestimonialsTab() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [showForm, setShowForm] = useState(false);

  const load = () => api.get("/coach/testimonials").then((r) => setItems(r.data)).catch((e) => toast.error(fmtErr(e)));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    await api.post("/coach/testimonials", form);
    toast.success("Testimonial saved as draft — publish it when approved by the client");
    setForm(EMPTY); setShowForm(false); load();
  };

  const set = async (id, patch) => { await api.put(`/coach/testimonials/${id}`, patch); load(); };

  return (
    <div data-testid="coach-testimonials">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display uppercase text-4xl">Testimonials <span className="text-zinc-600">({items.length})</span></h2>
        <button data-testid="testimonial-add-open" onClick={() => setShowForm(!showForm)}
          className="bg-volt text-black text-xs uppercase tracking-[0.16em] font-extrabold px-5 py-3 hover:bg-white transition-colors">
          + Add
        </button>
      </div>
      <p className="text-xs text-zinc-600 mb-6 max-w-xl">
        Workflow: positive client feedback → request permission → add here as draft → publish only with client approval.
        Private workout feedback never becomes public automatically.
      </p>

      {showForm && (
        <form onSubmit={create} data-testid="testimonial-form" className="border border-volt/40 bg-zinc-950 p-6 mb-6 grid sm:grid-cols-2 gap-3">
          <input required placeholder="Client name *" value={form.client_name} data-testid="tst-name"
            onChange={(e) => setForm({ ...form, client_name: e.target.value })}
            className="bg-ink border border-zinc-700 px-4 py-3 text-sm focus:border-volt outline-none" />
          <input placeholder="Program (e.g. Group Training)" value={form.program} data-testid="tst-program"
            onChange={(e) => setForm({ ...form, program: e.target.value })}
            className="bg-ink border border-zinc-700 px-4 py-3 text-sm focus:border-volt outline-none" />
          <textarea required placeholder="Testimonial *" rows={3} value={form.text} data-testid="tst-text"
            onChange={(e) => setForm({ ...form, text: e.target.value })}
            className="sm:col-span-2 bg-ink border border-zinc-700 px-4 py-3 text-sm focus:border-volt outline-none" />
          <input placeholder="Capability improvement (e.g. Squat 52 → 74)" value={form.capability_improvement} data-testid="tst-improvement"
            onChange={(e) => setForm({ ...form, capability_improvement: e.target.value })}
            className="bg-ink border border-zinc-700 px-4 py-3 text-sm focus:border-volt outline-none" />
          <button className="bg-volt text-black text-xs uppercase tracking-[0.16em] font-extrabold py-3 hover:bg-white transition-colors" data-testid="tst-save">
            Save draft
          </button>
        </form>
      )}

      <div className="space-y-3">
        {items.map((t) => (
          <div key={t.id} data-testid={`testimonial-${t.id}`} className="border border-zinc-800 bg-zinc-950 p-5">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="font-bold text-zinc-100">{t.client_name}
                  {t.demo && <span className="ml-2 text-[9px] uppercase tracking-[0.14em] text-zinc-500 border border-zinc-700 px-2 py-0.5">Demo content</span>}
                </p>
                <p className="text-xs text-zinc-500">{t.program}{t.capability_improvement ? ` · ${t.capability_improvement}` : ""}</p>
              </div>
              <span className={`text-[9px] uppercase tracking-[0.16em] font-extrabold px-2 py-1 ${t.status === "published" ? "bg-volt text-black" : "bg-zinc-800 text-zinc-400"}`}>
                {t.status}
              </span>
            </div>
            <p className="text-sm text-zinc-300 mt-3 italic">“{t.text}”</p>
            <div className="flex flex-wrap gap-3 mt-4">
              <button onClick={() => set(t.id, { status: t.status === "published" ? "draft" : "published" })}
                data-testid={`tst-publish-${t.id}`}
                className="text-xs uppercase tracking-[0.14em] font-bold text-zinc-400 hover:text-volt transition-colors">
                {t.status === "published" ? "Unpublish" : "Publish"}
              </button>
              <button onClick={() => set(t.id, { featured: !t.featured })} data-testid={`tst-feature-${t.id}`}
                className={`text-xs uppercase tracking-[0.14em] font-bold transition-colors ${t.featured ? "text-volt" : "text-zinc-500 hover:text-volt"}`}>
                <Star size={12} className="inline -mt-0.5" /> {t.featured ? "Featured" : "Feature"}
              </button>
              <button onClick={async () => { await api.delete(`/coach/testimonials/${t.id}`); load(); }}
                data-testid={`tst-delete-${t.id}`} className="text-zinc-600 hover:text-red-400 transition-colors">
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
        {!items.length && <p className="text-zinc-600 text-sm">No testimonials yet — collect them from client feedback with permission.</p>}
      </div>
    </div>
  );
}
